import { useSessionApp } from '../../../utils/actionableNotifications';
import { notificationDesign } from '../../../utils/notificationDesign';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useSocket } from "../../contexts/SocketContext";
import { useAccess } from "../../../features/access/context/AccessProvider";
import { PERMISSIONS } from "../../../features/access/constants/permissionKeys";
import { playChime, showNotification } from "../../misc_functions";
import notificationSound from "../../../assets/sounds/wati_notification.wav";
import uzdLogo from "../../../assets/images/uzd_logo_small.jpeg";
import { fetchDispatcherOrders } from "../models/dispatcher/dispatcherOrdersModel";

const OperationsNotificationsContext = createContext(null);
const EMPTY_CONTEXT = {
  enabled: false,
  activeData: null,
  activeRevision: 0,
  navigationTarget: null,
  refreshActive: () => Promise.resolve(null),
};

const eventData = payload => payload?.data ?? payload;

export function OperationsNotificationsProvider({ children }) {
  const { notification } = useSessionApp();
  const navigate = useNavigate();
  const { authState } = useAuth();
  const { socket, isConnected } = useSocket();
  const { can, status: accessStatus, revision: accessRevision, refreshAccess } = useAccess();
  const enabled = authState?.isAuthenticated
    && accessStatus === "ready"
    && can(PERMISSIONS.OPERATIONS_VIEW);

  const [activeData, setActiveData] = useState(null);
  const [activeRevision, setActiveRevision] = useState(0);
  const [navigationTarget, setNavigationTarget] = useState(null);
  const seenEventIdsRef = useRef(new Set());
  const browserNotificationsRef = useRef(new Map());
  const refreshTimerRef = useRef(null);
  const refreshPromiseRef = useRef(null);

  const refreshActive = useCallback(async () => {
    if (!enabled) return null;
    if (refreshPromiseRef.current) return refreshPromiseRef.current;
    const request = fetchDispatcherOrders({ view: "active" })
      .then(result => {
        setActiveData(result);
        setActiveRevision(current => current + 1);
        return result;
      })
      .catch(error => {
        if (error?.response?.status === 403) refreshAccess({ background: true }).catch(() => {});
        throw error;
      })
      .finally(() => { refreshPromiseRef.current = null; });
    refreshPromiseRef.current = request;
    return request;
  }, [enabled, refreshAccess]);

  const scheduleRefresh = useCallback(() => {
    if (!enabled) return;
    clearTimeout(refreshTimerRef.current);
    refreshTimerRef.current = setTimeout(() => refreshActive().catch(() => {}), 180);
  }, [enabled, refreshActive]);

  const navigateToOrder = useCallback(data => {
    const target = {
      event_id: data.event_id,
      queue: data.queue || null,
      operations_order_id: data.operations_order_id ?? null,
      query_pk: data.query_pk ?? null,
      query_id: data.query_id ?? null,
      entity_type: data.entity_type ?? null,
      entity_id: data.entity_id ?? null,
    };
    setNavigationTarget(target);
    if (target.operations_order_id != null) {
      navigate(`/operations-v2/orders/${target.operations_order_id}`);
      return;
    }
    const params = new URLSearchParams();
    if (target.query_pk != null) params.set("query_pk", target.query_pk);
    if (target.query_id != null) params.set("query_id", target.query_id);
    const search = params.toString();
    navigate(`/operations-v2${search ? `?${search}` : ""}`);
  }, [navigate]);

  useEffect(() => {
    if (!enabled || !socket) return undefined;

    const onRequired = payload => {
      const data = eventData(payload);
      if (!data?.event_id || seenEventIdsRef.current.has(data.event_id)) return;
      seenEventIdsRef.current.add(data.event_id);
      const openOrder = () => navigateToOrder(data);
      const title = data.title || "Operations action required";
      const body = data.message || payload?.message || "New Operations work needs attention.";

      notification.warning({
      ...notificationDesign('operations', 'warning'),
        key: data.event_id,
        message: title,
        description: body,
        duration: 0,
        placement: "bottomRight",
        onClick: openOrder,
        style: { cursor: "pointer" },
      });
      const browserNotification = showNotification(title, body, uzdLogo, {
        tag: data.event_id,
        onClick: openOrder,
      });
      if (browserNotification) browserNotificationsRef.current.set(data.event_id, browserNotification);
      playChime(notificationSound);
      scheduleRefresh();
    };

    const onResolved = payload => {
      const data = eventData(payload);
      if (!data?.event_id) return;
      notification.destroy(data.event_id);
      browserNotificationsRef.current.get(data.event_id)?.close?.();
      browserNotificationsRef.current.delete(data.event_id);
      seenEventIdsRef.current.delete(data.event_id);
      scheduleRefresh();
    };

    socket.on("operations_v2:action_required", onRequired);
    socket.on("operations_v2:action_resolved", onResolved);
    return () => {
      socket.off("operations_v2:action_required", onRequired);
      socket.off("operations_v2:action_resolved", onResolved);
    };
  }, [enabled, navigateToOrder, notification, scheduleRefresh, socket]);

  useEffect(() => {
    if (enabled && isConnected) scheduleRefresh();
  }, [accessRevision, enabled, isConnected, scheduleRefresh]);

  useEffect(() => {
    if (enabled) return undefined;
    clearTimeout(refreshTimerRef.current);
    seenEventIdsRef.current.forEach(eventId => notification.destroy(eventId));
    seenEventIdsRef.current.clear();
    browserNotificationsRef.current.forEach(item => item?.close?.());
    browserNotificationsRef.current.clear();
    setActiveData(null);
    setNavigationTarget(null);
    return undefined;
  }, [enabled, notification]);

  useEffect(() => () => {
    clearTimeout(refreshTimerRef.current);
    seenEventIdsRef.current.forEach(eventId => notification.destroy(eventId));
    browserNotificationsRef.current.forEach(item => item?.close?.());
  }, [notification]);

  const value = useMemo(() => ({
    enabled, activeData, activeRevision, navigationTarget, refreshActive,
  }), [activeData, activeRevision, enabled, navigationTarget, refreshActive]);

  return <OperationsNotificationsContext.Provider value={value}>{children}</OperationsNotificationsContext.Provider>;
}

export const useOperationsNotifications = () => useContext(OperationsNotificationsContext) || EMPTY_CONTEXT;
export { OperationsNotificationsContext };
